import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonPopup, ButtonVolver, Input } from "@/components/ui";
import Alerta from "@/components/alert/Alerta";
import { useProfesional } from "../context/ProfesionalContext";
import { useWhoWeAreEditor } from "./hook/useWhoWeAreEditor";
import { eliminarImagenEnServidor } from "@/components/utils";
import {
  subirImagenEquipoLanding,
  subirImagenSliderLanding,
  updateWhoWeAre,
} from "../subirImagen/subirImagen";

const EditarQuienesSomosLanding: FC = () => {
  const navigate = useNavigate();
  const { perfilId } = useProfesional();
  const MAX_SLIDER_IMAGES = 3;
  const MAX_EQUIPO = 10;
  const MAX_EXTRA = 10;
  const MAX_FAQ = 10;
  const MAX_VALORES = 8;
  const safePerfilId = perfilId ? String(perfilId) : undefined;
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const { who, loading, alert, setAlert, updateField, save } =
    useWhoWeAreEditor(safePerfilId);

  const { profesionalId, profesionId } = useParams();

  if (loading) {
    return <div className="text-white">Cargando...</div>;
  }

  // =========================
  // VALUES
  // =========================
  const updateValue = (index: number, value: string) => {
    const values = [...who.values];
    values[index] = value;
    updateField("values", values);
  };

  const addValue = () => {
    if (who.values.length >= MAX_VALORES) {
      setAlert({
        mensaje: `Solo puedes añadir un máximo de ${MAX_VALORES} valores.`,
        type: "error",
      });
      return;
    }

    updateField("values", [...who.values, ""]);
  };

  const removeValue = (index: number) => {
    updateField(
      "values",
      who.values.filter((_, i) => i !== index),
    );
  };

  // =========================
  // EXTRA POINTS
  // =========================
  const updateExtraPoint = (index: number, value: string) => {
    const points = [...who.extraPoints];
    points[index] = value;
    updateField("extraPoints", points);
  };

  const addExtraPoint = () => {
    if (who.extraPoints.length >= MAX_EXTRA) {
      setAlert({
        mensaje: `Solo puedes añadir un máximo de ${MAX_EXTRA} puntos destacados.`,
        type: "error",
      });
      return;
    }

    updateField("extraPoints", [...who.extraPoints, ""]);
  };

  const removeExtraPoint = (index: number) => {
    updateField(
      "extraPoints",
      who.extraPoints.filter((_, i) => i !== index),
    );
  };

  // =========================
  // FAQ
  // =========================
  const updateFaq = (
    index: number,
    field: "question" | "answer",
    value: string,
  ) => {
    const faq = [...who.faq];

    faq[index] = {
      ...faq[index],
      [field]: value,
    };

    updateField("faq", faq);
  };

  const addFaq = () => {
    if (who.faq.length >= MAX_FAQ) {
      setAlert({
        mensaje: `Solo puedes añadir un máximo de ${MAX_FAQ} preguntas frecuentes.`,
        type: "error",
      });
      return;
    }

    updateField("faq", [
      ...who.faq,
      {
        question: "",
        answer: "",
      },
    ]);
  };

  const removeFaq = (index: number) => {
    updateField(
      "faq",
      who.faq.filter((_, i) => i !== index),
    );
  };

  // =========================
  // TEAM
  // =========================
  const updateTeam = (
    index: number,
    field: "nombre" | "cargo" | "foto",
    value: string,
  ) => {
    const team = [...who.team];

    team[index] = {
      ...team[index],
      [field]: value,
    };

    updateField("team", team);
  };

  const addTeam = () => {
    if (who.team.length >= MAX_EQUIPO) {
      setAlert({
        mensaje: `Solo puedes añadir un máximo de ${MAX_EQUIPO} personas al equipo.`,
        type: "error",
      });

      return;
    }

    updateField("team", [...who.team, { nombre: "", cargo: "", foto: "" }]);
  };

  const removeTeam = (index: number) => {
    updateField(
      "team",
      who.team.filter((_, i) => i !== index),
    );
  };

  // =========================
  // SLIDER IMAGES
  // =========================
  /*     const updateSliderImage = (index: number, value: string) => {
            const images = [...who.sliderImages];
            images[index] = value;
            updateField("sliderImages", images);
        };
     */
  const addSliderImage = () => {
    if (who.sliderImages.length >= MAX_SLIDER_IMAGES) return;

    updateField("sliderImages", [...who.sliderImages, ""]);
  };

  const removeSliderImage = (index: number) => {
    updateField(
      "sliderImages",
      who.sliderImages.filter((_, i) => i !== index),
    );
  };

  /*===========
     Imagenes
    ============*/

  const handleSliderUpload = async (index: number, file: File) => {
    if (!perfilId) return;
    if (index >= MAX_SLIDER_IMAGES) return;

    setUploadingIndex(index);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Subir imagen
      const uploaded = await subirImagenSliderLanding(file, perfilId);

      // Actualizar estado
      const updated = [...who.sliderImages];
      updated[index] = uploaded.url;

      updateField("sliderImages", updated);

      // Guardar todo el objeto para no perder el equipo
      await updateWhoWeAre(perfilId, {
        ...who,
        sliderImages: updated.slice(0, MAX_SLIDER_IMAGES),
        team: who.team,
      });

      setAlert({
        mensaje: "Imagen subida correctamente",
        type: "success",
      });
    } catch (error) {
      console.error(error);

      setAlert({
        mensaje: "Error subiendo la imagen",
        type: "error",
      });
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleTeamUpload = async (index: number, file: File) => {
    if (!perfilId) return;

    try {
      // Subir imagen
      const uploaded = await subirImagenEquipoLanding(file, perfilId);

      // Actualizar estado
      const updated = [...who.team];

      updated[index] = {
        ...updated[index],
        foto: uploaded.url,
      };

      updateField("team", updated);

      // Guardar todo el objeto para no perder el slider
      await updateWhoWeAre(perfilId, {
        ...who,
        team: updated,
        sliderImages: who.sliderImages,
      });

      setAlert({
        mensaje: "Imagen del equipo subida correctamente",
        type: "success",
      });
    } catch (error) {
      console.error("Error subiendo equipo:", error);

      setAlert({
        mensaje: "Error subiendo la imagen del equipo",
        type: "error",
      });
    }
  };
  const deleteSliderImage = async (index: number) => {
    const img = who.sliderImages[index];

    if (img) {
      await eliminarImagenEnServidor(img, "imgLandingSlider");
    }

    removeSliderImage(index);
  };

  const deleteTeamPhoto = async (index: number) => {
    const foto = who.team[index]?.foto;

    if (foto) {
      await eliminarImagenEnServidor(foto, "imgLandingEquipo");
    }

    removeTeam(index);
  };

  const card = `relative overflow-hidden rounded-lg border-r-4 border-t-2 border-cyan-300/50 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl shadow-md shadow-zinc-800 p-8`;

  return (
    <div className=" text-zinc-900 py-16 px-4 overflow-x-hidden">
      <div className="space-y-10">
        <div className="absolute top-4 left-4 z-50">
          <ButtonVolver
            onClick={() =>
              navigate(
                `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
              )
            }
          />
        </div>

        <div
          className="
           w-full
           rounded-lg
           border-t-2
           border-b-2
           border-cyan-300
           bg-zinc-950
           text-white
           py-4
           px-8
           shadow-md
           shadow-zinc-800
           backdrop-blur-xl
         "
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold">Editor Quiénes Somos</h1>

              <p className="text-zinc-400 mt-2 max-w-md">
                Configura toda la información de la empresa.
              </p>
            </div>

            <div className="flex justify-end lg:justify-start">
              <ButtonPopup onClick={save} className="w-fit px-5 py-2">
                Guardar cambios
              </ButtonPopup>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* INTRO */}
          <section className={card}>
            <div className="space-y-5">
              <h2 className="text-xl font-bold">Introducción</h2>

              <Input
                placeholder="Título"
                value={who.title}
                onChange={(e) => updateField("title", e.target.value)}
              />

              <textarea
                rows={6}
                value={who.description}
                onChange={(e) => updateField("description", e.target.value)}
                className="w-full rounded-xl bg-zinc-900 text-white border border-white/10 p-4"
              />
            </div>
          </section>

          {/* MISIÓN / VISIÓN */}
          <section className={`${card} xl:col-span-2`}>
            <h2 className="text-xl font-bold mb-6">Misión y Visión</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <textarea
                value={who.mission}
                onChange={(e) => updateField("mission", e.target.value)}
                placeholder="Misión"
                className="
          w-full
          min-h-[220px]
          rounded-xl
          bg-zinc-900
          text-white
          border
          border-white/10
          p-4
        "
              />

              <textarea
                value={who.vision}
                onChange={(e) => updateField("vision", e.target.value)}
                placeholder="Visión"
                className="
          w-full
          min-h-[220px]
          rounded-xl
          bg-zinc-900
          text-white
          border
          border-white/10
          p-4
        "
              />
            </div>
          </section>

          {/* VALORES */}
          <section className={card}>
            <div className="space-y-5">
              <h2 className="text-xl font-bold">Valores</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {who.values.map((val, i) => (
                  <div key={i} className="flex gap-3">
                    <Input
                      value={val}
                      onChange={(e) => updateValue(i, e.target.value)}
                      className="flex-1"
                    />

                    <button
                      onClick={() => removeValue(i)}
                      className="px-4 rounded-lg bg-red-500 hover:bg-red-600"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-center">
                {who.values.length < MAX_VALORES ? (
                  <ButtonPopup onClick={addValue}>+ Añadir valor</ButtonPopup>
                ) : (
                  <p className="text-sm text-zinc-400 text-center">
                    Máximo {MAX_VALORES} valores
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* EXTRA */}
          <section className={card}>
            <div className="space-y-5">
              <h2 className="text-xl font-bold">Sección extra</h2>

              <Input
                placeholder="Título extra"
                value={who.extraTitle}
                onChange={(e) => updateField("extraTitle", e.target.value)}
              />

              <textarea
                rows={5}
                value={who.extraDescription}
                onChange={(e) =>
                  updateField("extraDescription", e.target.value)
                }
                className="w-full rounded-xl bg-zinc-900 text-white border border-white/10 p-4"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {who.extraPoints.map((point, i) => (
                  <div key={i} className="flex gap-3">
                    <Input
                      value={point}
                      onChange={(e) => updateExtraPoint(i, e.target.value)}
                      className="flex-1"
                    />

                    <button
                      onClick={() => removeExtraPoint(i)}
                      className="px-4 rounded-lg bg-red-500 hover:bg-red-600"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-center">
                {who.extraPoints.length < MAX_EXTRA ? (
                  <ButtonPopup onClick={addExtraPoint}>
                    + Añadir punto
                  </ButtonPopup>
                ) : (
                  <p className="text-sm text-zinc-400 text-center">
                    Máximo {MAX_EXTRA} puntos destacados
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className={`${card} xl:col-span-2`}>
            <div className="space-y-6">
              <h2 className="text-xl font-bold">FAQ</h2>

              <Input
                placeholder="Título FAQ"
                value={who.faqTitle}
                onChange={(e) => updateField("faqTitle", e.target.value)}
              />

              <div className="grid md:grid-cols-3 gap-5">
                {who.faq.map((item, i) => (
                  <div
                    key={i}
                    className="
                    rounded-lg
                    border
                    border-zinc-900
                    shadow-md
                    shadow-zinc-700
                    bg-zinc-900/50
                    p-5
                    space-y-4
                  "
                  >
                    <Input
                      placeholder="Pregunta"
                      value={item.question}
                      onChange={(e) => updateFaq(i, "question", e.target.value)}
                    />

                    <textarea
                      value={item.answer}
                      onChange={(e) => updateFaq(i, "answer", e.target.value)}
                      className="w-full rounded-xl bg-zinc-900 text-white border border-white/10 p-3"
                    />

                    <button
                      onClick={() => removeFaq(i)}
                      className="w-full rounded-lg bg-red-500 py-2"
                    >
                      Eliminar FAQ
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-center">
                {who.faq.length < MAX_FAQ ? (
                  <ButtonPopup onClick={addFaq}>+ Añadir FAQ</ButtonPopup>
                ) : (
                  <p className="text-sm text-zinc-400 text-center">
                    Máximo {MAX_FAQ} preguntas frecuentes
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* GALERÍA */}
          <section className={card}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Galería</h2>

              <span className="text-sm text-zinc-400">
                {who.sliderImages.length}/{MAX_SLIDER_IMAGES}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {who.sliderImages.map((img, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-zinc-700 border-r-4 border-t-2 shadow-md shadow-zinc-800 bg-lime-300/30 p-4 space-y-4"
                >
                  <img
                    src={img || "/icons/cuadranex.png"}
                    alt={`Imagen ${i + 1}`}
                    className="w-full aspect-video object-cover rounded-xl border border-white/10"
                  />

                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingIndex === i}
                    className="w-full text-sm"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      handleSliderUpload(i, file);
                    }}
                  />

                  {uploadingIndex === i && (
                    <p className="text-cyan-400 text-sm">
                      ⏳ Subiendo imagen...
                    </p>
                  )}

                  <button
                    onClick={async () => {
                      try {
                        await deleteSliderImage(i);

                        setAlert({
                          mensaje: "Imagen eliminada",
                          type: "success",
                        });
                      } catch (err) {
                        console.error(err);

                        setAlert({
                          mensaje: "Error eliminando imagen",
                          type: "error",
                        });
                      }
                    }}
                    className="
            w-full
            rounded-lg
            bg-red-500
            hover:bg-red-600
            transition
            py-2
            text-white
          "
                  >
                    Eliminar imagen
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6">
              {who.sliderImages.length < MAX_SLIDER_IMAGES ? (
                <ButtonPopup onClick={addSliderImage} className="px-2">
                  + Añadir imagen
                </ButtonPopup>
              ) : (
                <p className="text-center text-sm text-zinc-400">
                  Máximo {MAX_SLIDER_IMAGES} imágenes
                </p>
              )}
            </div>
          </section>

          {/* EQUIPO */}
          <section className={card}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Equipo</h2>

              <span className="text-sm text-zinc-400">
                {who.team.length} miembro{who.team.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {who.team.map((p, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-zinc-700 border-r-4 border-t-2 shadow-md shadow-zinc-800 bg-cyan-300/30 p-4 space-y-4"
                >
                  <div className="flex flex-col items-center gap-5">
                    <div className="w-32 h-32 rounded-full overflow-hidden border border-white/20">
                      <img
                        src={p.foto || "/icons/cuadranex.png"}
                        alt={p.nombre || `Persona ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      className="w-full text-sm"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        handleTeamUpload(i, file);
                      }}
                    />

                    <Input
                      value={p.nombre}
                      onChange={(e) => updateTeam(i, "nombre", e.target.value)}
                      placeholder="Nombre"
                      className="w-full"
                    />

                    <Input
                      value={p.cargo}
                      onChange={(e) => updateTeam(i, "cargo", e.target.value)}
                      placeholder="Cargo"
                      className="w-full"
                    />

                    <button
                      onClick={() => deleteTeamPhoto(i)}
                      className="
              w-full
              rounded-xl
              bg-red-500
              hover:bg-red-600
              transition
              py-2
              text-white
            "
                    >
                      Eliminar persona
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              {who.team.length < MAX_EQUIPO ? (
                <ButtonPopup onClick={addTeam} className="px-2">
                  + Añadir persona
                </ButtonPopup>
              ) : (
                <p className="text-center text-sm text-zinc-400">
                  Máximo {MAX_EQUIPO} personas
                </p>
              )}
            </div>
          </section>
        </div>

        {/* SAVE */}
        <div className="flex justify-end pt-10">
          <ButtonPopup onClick={save}>Guardar cambios</ButtonPopup>
        </div>

        {/* ALERT */}
        {alert && (
          <Alerta
            mensaje={alert.mensaje}
            type={alert.type}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
    </div>
  );
};

export default EditarQuienesSomosLanding;
